/**
 * useClient Hook
 *
 * This service contains logic to handle what "client" is active (e.g. "donate", "payin", "prize", etc).
 * Each client has distinct payment provider configuration.
 * This service streamlines access to this client.
 *
 * This hook may be called from React components and hooks
 *
 * Use to access:
 * * Current active clientId - determined by env var or overridden by ClientOverride context
 */

import { useContext, useCallback } from 'react';

import { ClientId } from './types';
import { ClientOverrideContext } from './clientOverrideContext';
import { useAppSelector } from '@/store/hooks';

const DEFAULT_CLIENT: ClientId = 'donate';

export function useClient() {
  const clientOverrideContext = useContext(ClientOverrideContext);

  // Rather than a context-based override, this is specifically an override set via URL param:
  const clientUrlOverride = useAppSelector(state => state.urlOverrides.clientOverride);

  const getClientId = useCallback((): ClientId => {
    // Given the specific use case, it feels like this should probably be given priority:
    if (clientUrlOverride) {
      return clientUrlOverride;
    }

    if (clientOverrideContext) {
      return clientOverrideContext;
    }

    const envClient = import.meta.env.VITE_CLIENT;
    if (envClient) {
      return envClient;
    }

    return DEFAULT_CLIENT;
  }, [clientOverrideContext, clientUrlOverride]);

  return {
    getClientId,
  };
}
